import { ref, useApi, useUserInfo, useCoreConfig, useDevice, useHeroConfig, useQuery, useQueryCache, nextTick, toValue, computed, isRefOrGetter } from '#imports'
import type { UseQueryOverrideOptions } from '@core/types'

export interface UseHeroOptions extends UseQueryOverrideOptions {
    /**
     * @description Page to get hero for
     */
    page?: string
    /**
     * @description Disable hero on mobile devices.
     */
    disableOnMobile?: boolean
    /**
     * @description Override page and look for specific hero from game slug.
     */
    gameSlug?: string
}

export interface UseHeroUpdateOptions extends UseHeroOptions {
    /**
     * @description Disable hero on mobile devices.
     * @default false
     */
    disableOnMobile?: boolean
}

export const useHero = (options: UseHeroOptions = {}) => {
    const { site } = useCoreConfig()
    const { serviceUrl } = useHeroConfig()
    const api = useApi({
        service: 'heroes'
    })
    const { isMobile } = useDevice()

    const { playerState } = useUserInfo()

    const { page: _page, disableOnMobile: _disableOnMobile, gameSlug: _gameSlug, ...queryOptions } = options

    const page = ref<string | undefined>(_page)
    const gameSlug = ref<string | undefined>(_gameSlug)
    const disableOnMobile = ref<boolean>(_disableOnMobile || false)

    const enabled = computed(() => {
        if (isRefOrGetter(queryOptions.enabled)) {
            const enabled = toValue(queryOptions.enabled)

            if (!enabled) return false
        }

        return !!page.value && !(toValue(disableOnMobile) && isMobile)
    })

    const key = computed(() => `hero:${page.value || 'none'}:${gameSlug.value || 'default'}:${playerState.value}`)
    const queryCache = useQueryCache()

    const query = useQuery<any>(async ({ signal }) => {
        return api(serviceUrl, {
            signal,
            query: {
                page: page.value,
                player_state: playerState.value,
                brand: site.brand,
                ...(gameSlug.value && { game_slug: gameSlug.value })
            }
        })
    }, {
        server: true,
        lazy: false,
        ...queryOptions,
        key,
        enabled
    })

    async function change(options: UseHeroUpdateOptions) {
        page.value = options.page
        gameSlug.value = options?.gameSlug ?? undefined
        disableOnMobile.value = options.disableOnMobile ?? false

        if (!enabled.value) return

        await nextTick()

        // refresh() bypasses the cache; a hero the layout (or SSR) fetched moments ago is still
        // fresh, so a page re-applying the same hero must not request it again (FRO-37).
        if (queryCache.get(key.value) !== undefined) return

        await query.refresh()
    }

    return query.enrich({
        page,
        gameSlug,
        change
    })
}
